import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, Children, useRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkGroup } from '@skatteetaten/ds-buttons';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { Icon, WarningSVGpath } from '@skatteetaten/ds-icons';
import { Heading } from '@skatteetaten/ds-typography';
import { ErrorSummaryContext } from '../ErrorSummaryContext/index.esm.js';
import { ErrorSummaryError } from '../ErrorSummaryError/index.esm.js';
import './styles.css';


const getErrorSummaryTitleAsDefault = ()=>'h2';

var styles = {
    "wrapper": "ErrorSummary-module_wrapper__RRuj2",
    "errorSummary": "ErrorSummary-module_errorSummary__Ez3Yr",
    "iconContainer": "ErrorSummary-module_iconContainer__XljZJ",
    "icon": "ErrorSummary-module_icon__SS16-",
    "summaryContainer": "ErrorSummary-module_summaryContainer__DazQR",
    "content": "ErrorSummary-module_content__JiGlY",
    "contentWithoutChildren": "ErrorSummary-module_contentWithoutChildren__OvZ99"
};

const ErrorSummary = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, content, shadowRootNode, title, titleAs = getErrorSummaryTitleAsDefault(), showErrorSummary, children }, ref)=>{
    const { t } = useTranslation('ds_forms', {
        i18n: dsI18n
    });
    const errors = Children.toArray(children);
    const contentWithoutChildrenClassName = !children ? styles.contentWithoutChildren : '';
    const errorSummaryRef = useRef(null);
    useImperativeHandle(ref, ()=>errorSummaryRef.current);
    useEffect(()=>{
        if (showErrorSummary) {
            errorSummaryRef.current?.focus();
        }
    }, [
        showErrorSummary
    ]);
    return /*#__PURE__*/ jsx("div", {
        ref: errorSummaryRef,
        className: styles.wrapper,
        id: id,
        lang: lang,
        "data-testid": dataTestId,
        "aria-live": 'assertive',
        tabIndex: -1,
        "aria-atomic": true,
        children: showErrorSummary && /*#__PURE__*/ jsxs("div", {
            className: `${styles.errorSummary} ${className}`.trim(),
            children: [
                /*#__PURE__*/ jsx("div", {
                    className: styles.iconContainer,
                    children: /*#__PURE__*/ jsx(Icon, {
                        svgPath: WarningSVGpath,
                        className: styles.icon,
                        size: 'large'
                    })
                }),
                /*#__PURE__*/ jsxs("div", {
                    className: styles.summaryContainer,
                    children: [
                        /*#__PURE__*/ jsx(Heading, {
                            as: titleAs,
                            level: 4,
                            hasSpacing: true,
                            children: title ?? t('errorsummary.Title', {
                                errorAmount: errors.length
                            })
                        }),
                        children && /*#__PURE__*/ jsx(ErrorSummaryContext.Provider, {
                            value: {
                                shadowRootNode
                            },
                            children: /*#__PURE__*/ jsx(LinkGroup, {
                                children: children
                            })
                        }),
                        content && /*#__PURE__*/ jsx("div", {
                            className: `${styles.content} ${contentWithoutChildrenClassName}`.trim(),
                            children: content
                        })
                    ]
                })
            ]
        })
    });
});
ErrorSummary.displayName = 'ErrorSummary';
ErrorSummary.Error = ErrorSummaryError;
ErrorSummary.Error.displayName = 'ErrorSummary.Error';

export { ErrorSummary, getErrorSummaryTitleAsDefault };
